import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Camera, Loader2 } from 'lucide-react';
import { listProjects, uploadImage, pollJob } from '../lib/api';
import { toast } from 'sonner';

const SYMBOLS = ['BTCUSD', 'ETHUSD', 'XAUUSD', 'SPX500', 'EURUSD', 'TSLA', 'AAPL', 'DXY'];
const INTERVALS = ['1', '5', '15', '60', '240', 'D', 'W', 'M'];

const SYMBOL_PREFIX = {
  BTCUSD: 'BINANCE:',
  ETHUSD: 'BINANCE:',
  TSLA: 'NASDAQ:',
  AAPL: 'NASDAQ:',
  SPX500: 'FOREXCOM:',
  EURUSD: 'FX:',
  DXY: 'TVC:',
  XAUUSD: 'FOREXCOM:',
};

const resolveTradingViewSymbol = (rawSymbol) => {
  const normalized = String(rawSymbol || '').toUpperCase();
  const prefix = SYMBOL_PREFIX[normalized] || 'BINANCE:';
  return `${prefix}${normalized}`;
};

export default function TradingViewPage() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const widgetWrapRef = useRef(null);
  const [symbol, setSymbol] = useState('BTCUSD');
  const [interval, setInterval] = useState('D');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [widgetHeight, setWidgetHeight] = useState(() => {
    if (typeof window === 'undefined') return 650;
    const raw = Number(window.localStorage.getItem('wavekit.tradingview.height') || 650);
    return Number.isFinite(raw) ? Math.max(420, Math.min(1200, raw)) : 650;
  });
  const [widgetWidthPct, setWidgetWidthPct] = useState(() => {
    if (typeof window === 'undefined') return 100;
    const raw = Number(window.localStorage.getItem('wavekit.tradingview.widthPct') || 100);
    return Number.isFinite(raw) ? Math.max(55, Math.min(100, raw)) : 100;
  });
  const widgetRef = useRef(null);
  const widgetResizeStateRef = useRef(null);
  const [isWidgetResizing, setIsWidgetResizing] = useState(false);

  useEffect(() => {
    listProjects().then(setProjects).catch(console.error);
  }, []);

  // Load TradingView widget
  useEffect(() => {
    if (!containerRef.current) return;

    // Clear previous widget
    containerRef.current.innerHTML = '';

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/tv.js';
    script.async = true;
    script.onload = () => {
      if (window.TradingView && containerRef.current) {
        widgetRef.current = new window.TradingView.widget({
          container_id: containerRef.current.id,
          symbol: resolveTradingViewSymbol(symbol),
          interval: interval,
          timezone: 'Etc/UTC',
          theme: 'dark',
          style: '1',
          locale: 'en',
          toolbar_bg: '#14161a',
          hide_side_toolbar: false,
          allow_symbol_change: true,
          save_image: true,
          width: '100%',
          height: '100%',
          backgroundColor: '#0e0f12',
          gridColor: 'rgba(255,255,255,0.03)',
        });
      }
    };
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [symbol, interval]);

  useEffect(() => {
    if (!widgetWrapRef.current || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const rect = entries?.[0]?.contentRect;
      const h = Math.round(rect?.height || 0);
      const w = Math.round(rect?.width || 0);

      if (h >= 420) {
        setWidgetHeight(h);
      }

      const parentWidth = widgetWrapRef.current?.parentElement?.clientWidth || 0;
      if (w > 0 && parentWidth > 0) {
        const pct = Math.max(55, Math.min(100, (w / parentWidth) * 100));
        setWidgetWidthPct(Number(pct.toFixed(1)));
      }
    });

    observer.observe(widgetWrapRef.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('wavekit.tradingview.height', String(Math.round(widgetHeight)));
  }, [widgetHeight]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('wavekit.tradingview.widthPct', String(Number(widgetWidthPct).toFixed(1)));
  }, [widgetWidthPct]);

  const clampWidgetHeight = useCallback((value) => Math.max(420, Math.min(1200, Math.round(Number(value) || 650))), []);

  const beginWidgetResize = useCallback((event) => {
    event.preventDefault();
    widgetResizeStateRef.current = {
      startY: event.clientY,
      startHeight: widgetHeight,
    };
    setIsWidgetResizing(true);
  }, [widgetHeight]);

  useEffect(() => {
    if (!isWidgetResizing) return undefined;

    const handlePointerMove = (event) => {
      const session = widgetResizeStateRef.current;
      if (!session) return;
      const nextHeight = session.startHeight + (event.clientY - session.startY);
      setWidgetHeight(clampWidgetHeight(nextHeight));
    };

    const stopResize = () => {
      widgetResizeStateRef.current = null;
      setIsWidgetResizing(false);
    };

    const previousCursor = document.body.style.cursor;
    const previousSelect = document.body.style.userSelect;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResize);
    window.addEventListener('pointercancel', stopResize);

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousSelect;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResize);
      window.removeEventListener('pointercancel', stopResize);
    };
  }, [clampWidgetHeight, isWidgetResizing]);


  const handleCaptureAndAnalyze = async (file) => {
    if (!selectedProject) {
      toast.error('Please select a project first');
      return;
    }

    setAnalyzing(true);
    setProgress(0);

    try {
      const { job_id } = await uploadImage(selectedProject, file, {
        uploadSource: 'tradingview_capture',
        analysisFocus: 'macro',
      });
      const job = await pollJob(job_id, (j) => setProgress(j.progress || 0));

      if (job.result_id) {
        toast.success('Analysis complete!');
        navigate(`/analysis/${selectedProject}?analysisId=${job.result_id}`);
      }
    } catch (e) {
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) handleCaptureAndAnalyze(file);
  };

  return (
    <div className="animate-fade-in" data-testid="tradingview-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-white">TradingView Chart</h1>
          <p className="text-sm text-[#a1a1aa] mt-1">View charts and capture screenshots for analysis</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-[11px] uppercase tracking-wider font-bold text-[#a1a1aa]">Symbol</label>
          <Select value={symbol} onValueChange={setSymbol}>
            <SelectTrigger className="w-32 bg-[#0e0f12] border-white/10 rounded-sm text-sm font-mono" data-testid="symbol-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#14161a] border-white/10">
              {SYMBOLS.map(s => (
                <SelectItem key={s} value={s} className="font-mono text-sm">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-[11px] uppercase tracking-wider font-bold text-[#a1a1aa]">Interval</label>
          <Select value={interval} onValueChange={setInterval}>
            <SelectTrigger className="w-24 bg-[#0e0f12] border-white/10 rounded-sm text-sm font-mono" data-testid="interval-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#14161a] border-white/10">
              {INTERVALS.map(i => (
                <SelectItem key={i} value={i} className="font-mono text-sm">{i}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-[11px] uppercase tracking-wider font-bold text-[#a1a1aa]">Height</label>
          <input
            type="range"
            min={420}
            max={1200}
            step={10}
            value={widgetHeight}
            onChange={(e) => setWidgetHeight(Number(e.target.value) || 650)}
            className="w-28 accent-[#f5b400]"
            data-testid="tradingview-height-slider"
          />
          <span className="font-mono text-xs text-[#f5b400] w-14 text-right">{widgetHeight}px</span>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-[11px] uppercase tracking-wider font-bold text-[#a1a1aa]">Width</label>
          <input
            type="range"
            min={55}
            max={100}
            step={1}
            value={widgetWidthPct}
            onChange={(e) => setWidgetWidthPct(Number(e.target.value) || 100)}
            className="w-28 accent-[#f5b400]"
            data-testid="tradingview-width-slider"
          />
          <span className="font-mono text-xs text-[#f5b400] w-12 text-right">{widgetWidthPct}%</span>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-52 bg-[#0e0f12] border-white/10 rounded-sm text-sm" data-testid="project-select">
              <SelectValue placeholder="Select project..." />
            </SelectTrigger>
            <SelectContent className="bg-[#14161a] border-white/10">
              {projects.map(p => (
                <SelectItem key={p.id} value={p.id} className="text-sm">{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <label className="cursor-pointer">
            <Button
              variant="default"
              className="bg-[#f5b400] text-black hover:bg-[#d9a000] rounded-sm text-xs"
              disabled={!selectedProject || analyzing}
              data-testid="upload-screenshot-btn"
              asChild
            >
              <span>
                {analyzing ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Camera className="w-3 h-3 mr-1.5" />}
                Upload Screenshot
              </span>
            </Button>
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleFileUpload} disabled={!selectedProject || analyzing} />
          </label>
        </div>
      </div>

      {analyzing && (
        <div className="mb-4 wk-panel p-3 flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin text-[#f5b400]" />
          <span className="text-sm text-[#a1a1aa]">Analyzing screenshot...</span>
          <span className="font-mono text-xs text-[#f5b400]">{progress}%</span>
        </div>
      )}

      {/* TradingView Widget */}
      <Card className="bg-[#14161a] border-white/5 overflow-hidden">
        <CardContent className="p-0">
          <div
            ref={widgetWrapRef}
            className="overflow-auto min-h-[420px] min-w-[55%] max-w-full"
            style={{ height: widgetHeight, width: `${widgetWidthPct}%` }}
            data-testid="tradingview-widget-wrap"
          >
            <div
              id="tradingview_widget"
              ref={containerRef}
              className="w-full h-full"
              data-testid="tradingview-widget"
            />
          </div>
        </CardContent>
      </Card>
      <div className="pt-2 flex justify-center">
        <button
          type="button"
          onPointerDown={beginWidgetResize}
          className={`h-5 w-36 rounded-full border font-mono text-[9px] transition-colors ${isWidgetResizing ? 'border-[#00f5d4]/60 bg-[#00f5d4]/15 text-[#00f5d4]' : 'border-white/10 bg-white/5 text-[#6b7280] hover:border-[#00f5d4]/35 hover:text-[#00f5d4]'} cursor-row-resize`}
          title="Drag to resize chart height"
        >
          drag chart height
        </button>
      </div>

      {/* Instructions */}
      <div className="mt-4 wk-panel p-4 relative">
        <div className="scanlines rounded-sm" />
        <div className="relative z-20">
          <h3 className="text-sm font-medium text-[#f5b400] mb-2">How to Use</h3>
          <ol className="text-xs text-[#a1a1aa] space-y-1.5 list-decimal list-inside">
            <li>Select your symbol and timeframe above</li>
            <li>Use TradingView's built-in tools to analyze the chart</li>
            <li>Take a screenshot using TradingView's camera icon or your OS screenshot tool</li>
            <li>Select a project from the dropdown, then click "Upload Screenshot"</li>
            <li>The image will be analyzed for Elliott Wave patterns</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

